import React, { useState } from 'react';
import Phone from 'react-phone-number-input';
import {
  GECKO_CONSTS,
  GeckoBtn,
  GeckoDialogService,
  GeckoModal,
  GeckoModalService,
  GeckoToastService,
} from 'ui';
import { createOptionsListFromRawOptions } from 'utilities/customFieldUtils';
import ReplyBoxField from 'components/ReplyBoxField';
import GeckoDates from 'services/gecko-date';
import { splitWithUnderscore } from 'utilities/fieldKeyUtils.js';
import { renderError } from 'services/gecko-error';
import GeckoRequest from 'services/gecko-request';
import { useRedux } from 'services/gecko-store/redux';
import PropTypes from 'prop-types';
import { CONSTS } from 'constants/index.js';
import SingleEntryOptionsList from '../SingleEntryOptionsList';
import {
  convertOptionToSaveFormat,
  getDefaultAsIdFromOptions,
  unarchiveOptionsWhereNewOptionsMatch,
} from '../SingleEntryOptionsList/logic';
import MultiEntryOptionsList from '../MultiEntryOptionsList';
import { getValidationErrors } from './validation';
import GeckoFeatures from 'services/gecko-features';
import GeckoStore from 'services/gecko-store';

const CONFIRM_EDIT_TYPE_COPY =
  'Changing the type will clear some of the fields. Continue?';
const CONFIRM_EDIT_ENTRY_MODE =
  'Changing data entry mode will clear some of the fields. Continue?';

const useFieldStateHook = (field) => {
  const isASelectType = (type) => {
    return [
      GECKO_CONSTS.FIELD_TYPE_SELECT,
      GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
    ].includes(type);
  };

  const getDefaultRawOptionState = () => {
    if (isASelectType(field.type) && !field.raw_options) {
      return '';
    }
    return field.raw_options;
  };

  const [rawOptions, setRawOptions] = useState(getDefaultRawOptionState());
  const [label, setLabel] = useState(field.label);
  const [key, setKey] = useState(field.key);
  const [type, setType] = useState(field.type);
  const [options, setOptions] = useState(field.field_options);
  const [requiredForSync, setRequiredForSync] = useState(
    field.required_for_sync
  );
  const [editorObject, setEditorObject] = useState(field.editor_object);
  const [defaultValue, setDefaultValue] = useState(field.default_value);
  const [singleEntryMode, setSingleEntryMode] = useState(
    field.id !== undefined
  );
  const [smartContextEnabled, setSmartContextEnabled] = useState(
    field.smart_context_enabled
  );

  const builtField = {
    custom_field: field.custom_field,
    default_value: defaultValue,
    editor_object: editorObject,
    field_options: options,
    id: field.id,
    key,
    label,
    raw_options: rawOptions,
    required_for_sync: requiredForSync,
    type,
    single_entry_mode: singleEntryMode,
    smart_context_enabled: smartContextEnabled,
  };

  const getDefaultResetValue = (newType = type) => {
    if (newType === GECKO_CONSTS.FIELD_TYPE_SELECT) return null;
    if (newType === GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI) return [];
    return '';
  };

  const resetFields = (newType) => {
    setEditorObject(undefined);
    setRawOptions('');
    setOptions([]);
    setDefaultValue(getDefaultResetValue(newType));
  };

  const areEmpty = (...fields) => {
    return fields.every((item) => !item || Object.keys(item).length === 0);
  };

  const setTypeAndResetFields = async (newType) => {
    if (areEmpty(editorObject, defaultValue, rawOptions)) {
      setType(newType);
      return;
    }

    withConfirmationDialog(() => {
      setType(newType);
      resetFields(newType);
    }, CONFIRM_EDIT_TYPE_COPY);
  };

  const setSingleEntryModeAndResetFields = async (newMode) => {
    if (areEmpty(editorObject, defaultValue, rawOptions, options)) {
      setSingleEntryMode(newMode);
      return;
    }

    withConfirmationDialog(() => {
      setSingleEntryMode(newMode);
      resetFields();
    }, CONFIRM_EDIT_ENTRY_MODE);
  };

  const getBuiltField = () => builtField;

  return {
    ...builtField,
    getBuiltField,
    setDefaultValue,
    setEditorObject,
    setKey,
    setLabel,
    setOptions,
    setRawOptions,
    setRequiredForSync,
    setType: setTypeAndResetFields,
    setSingleEntryMode: setSingleEntryModeAndResetFields,
    setSmartContextEnabled,
  };
};

const withConfirmationDialog = async (next, msg) => {
  const confirmed = await new GeckoDialogService().confirm(msg);
  if (confirmed) {
    next();
  }
};

const CustomFieldEditModal = (props) => {
  const field = useFieldStateHook(props.field);
  const fields = useRedux((state) => state.fields);

  const isStandardField = field.custom_field === false;
  const isEditingExistingField = Boolean(props.field.id);

  const isBasicField = (field) =>
    [
      GECKO_CONSTS.FIELD_TYPE_TEXT,
      GECKO_CONSTS.FIELD_TYPE_TEXTAREA,
      GECKO_CONSTS.FIELD_TYPE_NUMBER,
      GECKO_CONSTS.FIELD_TYPE_EMAIL,
    ].includes(field.type);

  const entryModeOptions = ({ name, setSingleEntry }) => ({
    key: 'single_entry_mode',
    type: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
    width: '100%',
    hideWhen: () => isEditingExistingField,
    render: () => (
      <GeckoBtn
        className="pull-right"
        name={name}
        handler={() => field.setSingleEntryMode(setSingleEntry)}
      />
    ),
  });

  const getMultiEntrySelectInput = ({ label, type }) => [
    {
      key: 'raw_options',
      component: (
        <MultiEntryOptionsList
          updateState={updateRawOptions}
          rawOptions={field.raw_options}
          options={field.field_options}
          type={type}
          defaultValues={field.default_value}
          swapModeButton={entryModeOptions({
            name: 'Single Entry Mode',
            setSingleEntry: true,
          })}
        />
      ),
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: '100%',
      label,
      description:
        'If you need your option to have a different value for your integration(s), update the text within the value input for the option.',
      required: true,
    },
  ];

  const getSingleEntrySelectInput = ({ label, type }) => [
    {
      key: 'field_options',
      component: (
        <SingleEntryOptionsList
          updateState={updateOptions}
          options={field.field_options}
          type={type}
          defaultValues={field.default_value}
          usedValues={props.usedValues}
          swapModeButton={entryModeOptions({
            name: 'Multiple Entry Mode',
            setSingleEntry: false,
          })}
        />
      ),
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: '100%',
      label,
      description:
        'If you need your option to have a different value for your integration(s), update the text within the value input for the option.',
      required: true,
    },
  ];

  const getFields = () => {
    const fieldOptions = [
      {
        key: 'label',
        label: 'Label',
        type: GECKO_CONSTS.FIELD_TYPE_TEXT,
        description:
          'The name of the Contact Field as it appears to all users.',
        required: true,
        width: '100',
        onBlur: () => {
          if (!field.key && field.label) {
            const key = splitWithUnderscore(field.label);
            onFieldChange('key', key);
          }
        },
      },
      {
        key: 'key',
        label: 'Key',
        description:
          'A unique name that will distinguish this field in the database from every other field. You cannot use spaces, capital letters or special characters.',
        width: '100',
        disabledWhen: () => isStandardField,
        required: true,
      },
      {
        key: 'type',
        label: 'Type',
        width: '100',
        disabledWhen: () => isEditingExistingField || isStandardField,
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
        config: {
          clearable: false,
        },
        options: [
          {
            label: 'Text (Single Line)',
            value: GECKO_CONSTS.FIELD_TYPE_TEXT,
          },
          {
            label: 'Text (Multiple Lines)',
            value: GECKO_CONSTS.FIELD_TYPE_TEXTAREA,
          },
          {
            label: 'Email',
            value: GECKO_CONSTS.FIELD_TYPE_EMAIL,
          },
          {
            label: 'Date',
            value: GECKO_CONSTS.FIELD_TYPE_DATE,
          },
          {
            label: 'Telephone Number',
            value: GECKO_CONSTS.FIELD_TYPE_TELEPHONE,
          },
          {
            label: 'Number',
            value: GECKO_CONSTS.FIELD_TYPE_NUMBER,
          },
          {
            label: 'Single Choice',
            value: GECKO_CONSTS.FIELD_TYPE_SELECT,
          },
          {
            label: 'Multiple Choice',
            value: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
          },
          {
            label: 'Display Statement',
            value: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
          },
        ],
      },
    ];

    if (field.type === GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI) {
      const options = {
        label: 'Multiple Options',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
      };
      const newFields = field.single_entry_mode
        ? getSingleEntrySelectInput(options)
        : getMultiEntrySelectInput(options);

      fieldOptions.push(...newFields);
    }

    if (field.type === GECKO_CONSTS.FIELD_TYPE_SELECT) {
      const options = {
        label: 'Single Choice',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      };
      const newFields = field.single_entry_mode
        ? getSingleEntrySelectInput(options)
        : getMultiEntrySelectInput(options);

      fieldOptions.push(...newFields);
    }

    if (field.type === GECKO_CONSTS.FIELD_TYPE_DISPLAY) {
      fieldOptions.push({
        label: 'Display Statement',
        key: 'editor_object',
        required: true,
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        autoResize: true,
        description:
          'Add your privacy/ data protection policy information here to refer it to users in a conversation.',
        component: (
          <ReplyBoxField
            formatAsEditorObject
            editorMode={CONSTS.EDITOR_MODE_TYPE.RICH}
            toolbarClassName={'display'}
            compileTemplateTags={false}
            noDragUpload={true}
            meta={{ attachments: { show: true } }}
          />
        ),
      });
    }

    if (
      field.type === GECKO_CONSTS.FIELD_TYPE_TELEPHONE ||
      field.key === 'tel'
    ) {
      fieldOptions.push({
        label: 'Default Value',
        key: 'default_value',
        description:
          'If set any new Contacts that are created without this field filled in will be given this default value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <Phone placeholder="Enter phone number" />,
        width: '100%',
      });
    }

    if (field.type === GECKO_CONSTS.FIELD_TYPE_DATE) {
      fieldOptions.push({
        label: 'Default Value',
        key: 'default_value',
        description:
          'If set any new Contacts that are created without this field filled in will be given this default value',
        type: GECKO_CONSTS.FIELD_TYPE_DATE,
        width: '100%',
        format: 'X',
        enableUTC: true,
        display_format: new GeckoDates().FORMATS.SHORT,
        placeholder: new GeckoDates().FORMATS.SHORT,
      });
    }

    // If we haven't seen this field type just try and render it as a GeckoField
    if (isBasicField(field)) {
      fieldOptions.push({
        label: 'Default Value',
        key: 'default_value',
        description:
          'If set any new Contacts that are created without this field filled in will be given this default value',
        type: field.type,
        options: field.field_options,
        width: '100%',
      });
    }

    fieldOptions.push({
      key: 'required_for_sync',
      label: 'Required for Sync',
      description:
        'Chat will only sync a Contact or Conversation if this field is populated.',
      width: '100%',
      type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
    });

    if (
      GeckoStore.redux.get('appState.isSupport') && // This will get removed soon
      GeckoFeatures.can('ai-engine')
    ) {
      fieldOptions.push({
        key: 'smart_context_enabled',
        label: 'Smart Context Enabled',
        description:
          'ChatGPT will use this field, if populated, to give context to a message. Only one field can be enabled at a time.',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
        disabledWhen: () =>
          fields.some(
            (_field) => _field.smart_context_enabled && _field.id !== field?.id
          ),
      });
    }

    return fieldOptions;
  };

  const updateRawOptions = (rawOptions, defaultValue) => {
    const optionsList = createOptionsListFromRawOptions(rawOptions);
    field.setRawOptions(rawOptions);
    let realdefaultValue = undefined;
    if (defaultValue && Array.isArray(defaultValue)) {
      realdefaultValue = defaultValue.map(
        (value) => optionsList.find((option) => option.value === value).id
      );
    } else if (defaultValue) {
      realdefaultValue = optionsList.find(
        (option) => option.value === defaultValue
      ).id;
    }

    updateOptions(optionsList, realdefaultValue);
  };

  const updateOptions = (options, defaultValues) => {
    field.setOptions(options);
    field.setDefaultValue(defaultValues);
  };

  const onFieldChange = (key, value) => {
    switch (key) {
      case 'label':
        field.setLabel(value);
        break;
      case 'default_value':
        field.setDefaultValue(value);
        break;
      case 'field_options':
        field.setOptions(value);
        break;
      case 'key':
        field.setKey(value);
        break;
      case 'required_for_sync':
        field.setRequiredForSync(value);
        break;
      case 'editor_object':
        field.setEditorObject(value);
        break;
      case 'type':
        field.setType(value);
        break;
      case 'smart_context_enabled':
        field.setSmartContextEnabled(value);
        break;
      default:
        return;
    }
  };

  const formatFieldOptionsForSave = (options = []) =>
    unarchiveOptionsWhereNewOptionsMatch(options).map(
      convertOptionToSaveFormat
    );

  const isTypeWithOptions = (type) =>
    type === GECKO_CONSTS.FIELD_TYPE_SELECT ||
    type === GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI;

  const saveField = (endpoint) => {
    const builtField = field.getBuiltField();
    if (!builtField.single_entry_mode && builtField.field_options) {
      builtField.default_value = getDefaultAsIdFromOptions(
        builtField.default_value,
        builtField.field_options,
        builtField.type
      );
    }

    const formattedOptions = isTypeWithOptions(field.type)
      ? formatFieldOptionsForSave(builtField.field_options)
      : [];
    builtField.field_options =
      formattedOptions.length > 0 ? formattedOptions : undefined;

    return GeckoRequest.post(endpoint, builtField);
  };

  const createField = () => saveField(`/fields`);
  const updateField = () => saveField(`/fields/${field.id}`);

  const buttonHandler = (successMessage, saveFunction) => async () => {
    try {
      const validationError = getValidationErrors(field, fields);
      if (validationError) {
        return renderError(validationError);
      }
      await saveFunction();
      new GeckoToastService().success(successMessage);
      new GeckoModalService().close(true);
    } catch (error) {
      renderError(error);
    }
  };

  const updateHandler = buttonHandler(
    `Field ${field.label} updated`,
    updateField
  );
  const createHandler = buttonHandler(
    `Field ${field.label} created`,
    createField
  );

  const createButton = {
    name: 'Add Field',
    preset: GECKO_CONSTS.BTN_PRESET_SAVE,
    primary: true,
    handler: createHandler,
  };
  const updateButton = {
    name: 'Save Changes',
    preset: GECKO_CONSTS.BTN_PRESET_SAVE,
    primary: true,
    handler: updateHandler,
  };

  const modalButtons = isEditingExistingField ? [updateButton] : [createButton];

  return (
    <div className="CustomFieldEditModal">
      <GeckoModal
        name={isEditingExistingField ? 'Update Field' : 'Create Field'}
        fields={getFields()}
        values={field}
        handler={onFieldChange}
        btns={modalButtons}
      />
    </div>
  );
};

CustomFieldEditModal.propTypes = {
  field: PropTypes.shape({
    raw_options: PropTypes.string,
    label: PropTypes.string,
    key: PropTypes.string,
    type: PropTypes.string,
    field_options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    custom_field: PropTypes.bool,
    required_for_sync: PropTypes.bool,
    order: PropTypes.number,
    editor_object: PropTypes.object,
    default_value: PropTypes.any,
    smart_context_enabled: PropTypes.bool,
  }),
  usedValues: PropTypes.arrayOf(PropTypes.string),
};

CustomFieldEditModal.defaultProps = {
  field: {
    label: '',
    key: '',
    type: 'text',
    required_for_sync: false,
    custom_field: true,
    smart_context_enabled: false,
  },
  usedValues: [],
};

export default CustomFieldEditModal;
